/**
 * Admin User Management - with custom role assignment
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Key, Ban, Trash2, X, Check, AlertTriangle, Upload, KeyRound, UserPlus } from 'lucide-react';
import axios from 'axios';
import useStore from '../../store/useStore';

const AVATAR_COLORS = ['#007AFF','#34C759','#FF375F','#FF9500','#C77DFF','#FF9A3C','#4ECDC4','#F72585'];

function Badge({ text, color, bg }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold"
      style={{ background: bg, color }}>
      {text}
    </span>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.40)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        style={{ background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(0,122,255,0.20)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-n-800 text-base">{title}</h3>
          <button onClick={onClose} className="text-n-500 hover:text-n-700"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function InputField({ label, ...props }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">{label}</label>
      <input
        className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none transition-all"
        style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
        {...props}
      />
    </div>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'user', avatarColor: '#007AFF', customRoleId: '' });
  const [pwForm, setPwForm] = useState({ newPassword: '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const addToast = useStore(s => s.addToast);
  const [selected, setSelected] = useState(new Set());
  const [allChannels, setAllChannels] = useState([]);
  const [assignedChannelIds, setAssignedChannelIds] = useState([]);
  const [primaryChannelId, setPrimaryChannelId] = useState('');

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const [usersRes, rolesRes, channelsRes] = await Promise.all([
        axios.get('/api/admin/users', { params }),
        axios.get('/api/admin/roles'),
        axios.get('/api/admin/channels'),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setAllChannels(channelsRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ username: '', email: '', password: '', role: 'user', avatarColor: '#007AFF', customRoleId: '' });
    setErr('');
    setModal('create');
  };

  // ── Bulk modals state (Feature #9) ────────────────────────────────────────
  const [csvText, setCsvText] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [bulkInviteEmails, setBulkInviteEmails] = useState('');
  const [bulkInviteRole, setBulkInviteRole] = useState('user');
  const [bulkResetResults, setBulkResetResults] = useState(null);

  const openImport = () => {
    setCsvText(''); setImportResults(null); setErr(''); setModal('import');
  };
  const openBulkInvite = () => {
    setBulkInviteEmails(''); setImportResults(null); setErr(''); setModal('bulk-invite');
  };
  const openBulkReset = () => {
    if (selected.size === 0) {
      addToast('Selecciona usuarios primero', 'error'); return;
    }
    setBulkResetResults(null); setErr(''); setModal('bulk-reset');
  };

  // Tiny CSV parser — assumes header row, simple split by comma; trims.
  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length < 1) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split(',').map(c => c.trim());
      const row = {};
      headers.forEach((h, idx) => { row[h] = cells[idx] || ''; });
      if (row.username || row.email) out.push(row);
    }
    return out;
  };

  const handleImportSubmit = async () => {
    if (!csvText.trim()) { setErr('Pegá CSV o subí un archivo'); return; }
    const rows = parseCsv(csvText);
    if (rows.length === 0) { setErr('No se detectaron filas válidas'); return; }
    setSaving(true); setErr('');
    try {
      const r = await axios.post('/api/admin/users/import', { users: rows });
      setImportResults(r.data);
      load();
      addToast(`${r.data.created}/${r.data.count} importados`, 'success');
    } catch (e) {
      setErr(e.response?.data?.error || 'Error');
    } finally { setSaving(false); }
  };

  const handleBulkInvite = async () => {
    const emails = bulkInviteEmails.split(/[,\n;\s]+/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) { setErr('Ingresá al menos un email'); return; }
    const rows = emails.map(email => {
      const username = email.split('@')[0].replace(/[^a-z0-9_-]/gi, '').slice(0, 50) || ('user_' + Math.random().toString(36).slice(2, 6));
      return { username, email, role: bulkInviteRole };
    });
    setSaving(true); setErr('');
    try {
      const r = await axios.post('/api/admin/users/import', { users: rows });
      setImportResults(r.data);
      load();
      addToast(`${r.data.created}/${r.data.count} invitados`, 'success');
    } catch (e) {
      setErr(e.response?.data?.error || 'Error');
    } finally { setSaving(false); }
  };

  const handleBulkReset = async () => {
    setSaving(true); setErr('');
    try {
      const r = await axios.post('/api/admin/users/bulk-reset', { userIds: Array.from(selected) });
      setBulkResetResults(r.data);
      addToast(`${r.data.results.filter(x => x.ok).length} contraseñas reseteadas`, 'success');
    } catch (e) {
      setErr(e.response?.data?.error || 'Error');
    } finally { setSaving(false); }
  };

  const handleCsvFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(String(e.target.result || ''));
    reader.readAsText(file);
  };

  const openEdit = async (u) => {
    setSelectedUser(u);
    setForm({
      username: u.username,
      email: u.email,
      role: u.role,
      avatarColor: u.avatarColor,
      customRoleId: u.customRoleId || '',
    });
    setAssignedChannelIds([]);
    setPrimaryChannelId('');
    setErr('');
    setModal('edit');
    // Load assigned channels for this user
    try {
      const res = await axios.get(`/api/admin/users/${u.id}/channels`);
      const ids = res.data.map(c => c.id);
      const primary = res.data.find(c => c.isPrimary);
      setAssignedChannelIds(ids);
      setPrimaryChannelId(primary?.id || '');
    } catch (e) { /* non-critical */ }
  };

  const openResetPw = (u) => {
    setSelectedUser(u);
    setPwForm({ newPassword: '' });
    setErr('');
    setModal('reset-pw');
  };

  const openDelete = (u) => {
    setSelectedUser(u);
    setErr('');
    setModal('delete');
  };

  const closeModal = () => { setModal(null); setSelectedUser(null); setErr(''); };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await axios.post('/api/admin/users', {
        ...form,
        customRoleId: form.customRoleId ? parseInt(form.customRoleId) : null,
      });
      closeModal(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await axios.put(`/api/admin/users/${selectedUser.id}`, {
        username: form.username,
        email: form.email,
        role: form.role,
        avatarColor: form.avatarColor,
        customRoleId: form.customRoleId ? parseInt(form.customRoleId) : null,
      });
      // Save channel assignments
      await axios.put(`/api/admin/users/${selectedUser.id}/channels`, {
        channelIds: assignedChannelIds,
        primaryChannelId: primaryChannelId || null,
      });
      closeModal(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleResetPw = async (e) => {
    e.preventDefault();
    if (!pwForm.newPassword || pwForm.newPassword.length < 6) {
      setErr('Mínimo 6 caracteres'); return;
    }
    setSaving(true); setErr('');
    try {
      await axios.post(`/api/admin/users/${selectedUser.id}/reset-password`, { newPassword: pwForm.newPassword });
      closeModal();
    } catch (e) { setErr(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true); setErr('');
    try {
      await axios.delete(`/api/admin/users/${selectedUser.id}`);
      closeModal(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleBan = async (u) => {
    try {
      await axios.post(`/api/admin/users/${u.id}/ban`);
      load();
    } catch (e) { addToast(e.response?.data?.error || 'Error al banear usuario', 'error'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!(await useStore.getState().askConfirm({ title: `Eliminar ${selected.size} usuario(s)`, message: 'Esta acción no se puede deshacer.', confirmText: 'Eliminar', danger: true }))) return;
    try {
      await Promise.all([...selected].map(id => axios.delete(`/api/admin/users/${id}`)));
      setSelected(new Set());
      load();
    } catch (e) { addToast('Error en eliminación masiva', 'error'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX') : '—';
  const formatLast = (d) => {
    if (!d) return '—';
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return formatDate(d);
  };

  // Role form fields shared between create/edit
  const RoleFields = () => (
    <>
      <div>
        <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Rol de sistema</label>
        <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}
          className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 outline-none"
          style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}>
          <option value="user">Usuario</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      {roles.length > 0 && (
        <div>
          <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Rol personalizado (para acceso por canales)</label>
          <select value={form.customRoleId} onChange={e => setForm(f => ({...f, customRoleId: e.target.value}))}
            className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 outline-none"
            style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}>
            <option value="">— Sin rol personalizado —</option>
            {roles.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
      )}
    </>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-n-800">Usuarios</h1>
          <p className="text-sm text-n-500 mt-0.5">{users.length} usuario(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openImport}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-n-700 transition-all"
            style={{ background: '#FFF3DE', border: '1px solid rgba(255,159,10,0.20)' }}>
            <Upload size={14} /> Importar CSV
          </button>
          <button onClick={openBulkInvite}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-n-700 transition-all"
            style={{ background: '#E5F9EC', border: '1px solid rgba(52,199,89,0.20)' }}>
            <UserPlus size={14} /> Invitar bulk
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white transition-all"
            style={{ background: 'linear-gradient(135deg,#007AFF,#AF52DE)', boxShadow: '0 8px 22px rgba(0,122,255,.30)' }}>
            <Plus size={14} /> Nuevo Usuario
          </button>
        </div>
      </div>

      <div className="flex gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-n-500" />
          <input type="text" placeholder="Buscar usuario..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl text-sm text-n-800 outline-none"
            style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.15)' }} />
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          className="px-3 py-2 rounded-xl text-sm text-n-800 outline-none"
          style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.15)' }}>
          <option value="">Todos los roles</option>
          <option value="admin">Admin</option>
          <option value="user">Usuario</option>
        </select>
        {selected.size > 0 && (
          <>
            <button onClick={openBulkReset}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-s-orange transition-all"
              style={{ background: 'rgba(255,159,10,0.12)', border: '1px solid rgba(255,159,10,0.20)' }}>
              <KeyRound size={14} /> Reset PW ({selected.size})
            </button>
            <button onClick={bulkDelete}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-s-red transition-all"
              style={{ background: 'rgba(255,55,95,0.12)', border: '1px solid rgba(255,55,95,0.20)' }}>
              <Trash2 size={14} /> Eliminar ({selected.size})
            </button>
          </>
        )}
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
        <table className="w-full">
          <thead>
            <tr style={{ background: '#F5F5F7', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <th className="px-4 py-3 text-left">
                <input type="checkbox"
                  onChange={e => setSelected(e.target.checked ? new Set(users.map(u => u.id)) : new Set())}
                  className="w-3.5 h-3.5 rounded accent-brand-500" />
              </th>
              {['Usuario', 'Email', 'Rol', 'Rol Custom', 'Estado', 'Creado', 'TX', 'Acciones'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-bold text-n-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="px-4 py-8 text-center text-n-400">Cargando...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="9" className="px-4 py-8 text-center text-n-400">No hay usuarios</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className="hover:bg-black/[0.02] transition-colors"
                style={{ borderBottom: '1px solid #F5F5F7' }}>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)}
                    className="w-3.5 h-3.5 rounded accent-brand-500"
                    disabled={u.username === 'admin'} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{ background: u.avatarColor }}>
                      {(u.username?.[0] || '?').toUpperCase()}
                    </div>
                    <span className="text-sm font-semibold text-n-800">{u.username}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-n-600">{u.email}</td>
                <td className="px-4 py-3">
                  <Badge text={u.role} color={u.role === 'admin' ? '#FF375F' : '#007AFF'}
                    bg={u.role === 'admin' ? 'rgba(255,55,95,0.12)' : 'rgba(0,0,0,0.06)'} />
                </td>
                <td className="px-4 py-3">
                  {u.customRole ? (
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: (u.customRole.color || '#007AFF') + '22', color: u.customRole.color || '#007AFF', border: `1px solid ${(u.customRole.color || '#007AFF')}44` }}>
                      {u.customRole.name}
                    </span>
                  ) : <span className="text-xs text-n-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  {u.banned
                    ? <Badge text="Baneado" color="#FF375F" bg="rgba(255,55,95,0.12)" />
                    : u.isOnline
                    ? <Badge text="Online" color="#34C759" bg="rgba(52,199,89,0.12)" />
                    : <Badge text="Offline" color="#64748b" bg="rgba(100,116,139,0.1)" />
                  }
                </td>
                <td className="px-4 py-3 text-xs text-n-500">{formatDate(u.createdAt)}</td>
                <td className="px-4 py-3 text-xs text-n-600">{u.transmissionCount || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(u)} title="Editar"
                      className="p-1.5 rounded-lg text-n-500 hover:text-s-blue hover:bg-s-blue/10 transition-all">
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => openResetPw(u)} title="Reset contraseña"
                      className="p-1.5 rounded-lg text-n-500 hover:text-s-orange hover:bg-s-orange/10 transition-all">
                      <Key size={13} />
                    </button>
                    {u.username !== 'admin' && (
                      <>
                        <button onClick={() => handleBan(u)} title={u.banned ? 'Desbanear' : 'Banear'}
                          className={`p-1.5 rounded-lg transition-all ${u.banned ? 'text-green-500 hover:bg-s-green/10' : 'text-n-500 hover:text-orange-400 hover:bg-orange-500/10'}`}>
                          <Ban size={13} />
                        </button>
                        <button onClick={() => openDelete(u)} title="Eliminar"
                          className="p-1.5 rounded-lg text-n-500 hover:text-s-red hover:bg-s-red/10 transition-all">
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {modal === 'create' && (
        <Modal title="Crear Usuario" onClose={closeModal}>
          <form onSubmit={handleCreate} className="space-y-4">
            <InputField label="Usuario" value={form.username} onChange={e => setForm(f => ({...f, username: e.target.value}))} required minLength={3} maxLength={50} />
            <InputField label="Email" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required />
            <InputField label="Contraseña" type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} required minLength={6} />
            <RoleFields />
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Color Avatar</label>
              <div className="flex flex-wrap gap-2">
                {AVATAR_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setForm(f => ({...f, avatarColor: c}))}
                    className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                    style={{ background: c, outline: form.avatarColor === c ? '2px solid white' : 'none', outlineOffset: '2px' }} />
                ))}
              </div>
            </div>
            {err && <p className="text-sm text-s-red">{err}</p>}
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-800 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
                {saving ? '...' : 'Crear'}
              </button>
              <button type="button" onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600 hover:text-n-800"
                style={{ background: 'rgba(255,255,255,0.05)' }}>
                Cancelar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === 'edit' && selectedUser && (
        <Modal title={`Editar: ${selectedUser.username}`} onClose={closeModal}>
          <form onSubmit={handleEdit} className="space-y-4">
            <InputField label="Usuario" value={form.username} onChange={e => setForm(f => ({...f, username: e.target.value}))} required />
            <InputField label="Email" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required />
            <RoleFields />
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Color Avatar</label>
              <div className="flex flex-wrap gap-2">
                {AVATAR_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setForm(f => ({...f, avatarColor: c}))}
                    className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                    style={{ background: c, outline: form.avatarColor === c ? '2px solid white' : 'none', outlineOffset: '2px' }} />
                ))}
              </div>
            </div>
            {allChannels.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Canales asignados</label>
                <div className="rounded-xl p-3 space-y-2 max-h-48 overflow-y-auto"
                  style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}>
                  {allChannels.map(ch => {
                    const isChecked = assignedChannelIds.includes(ch.id);
                    const isPrimary = primaryChannelId === ch.id;
                    return (
                      <div key={ch.id} className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id={`ch-${ch.id}`}
                          checked={isChecked}
                          onChange={e => {
                            if (e.target.checked) {
                              setAssignedChannelIds(prev => [...prev, ch.id]);
                            } else {
                              setAssignedChannelIds(prev => prev.filter(id => id !== ch.id));
                              if (primaryChannelId === ch.id) setPrimaryChannelId('');
                            }
                          }}
                          className="w-3.5 h-3.5 rounded accent-brand-500"
                        />
                        <label htmlFor={`ch-${ch.id}`} className="flex-1 text-sm text-n-700 cursor-pointer">{ch.name}</label>
                        {isChecked && (
                          <label className="flex items-center gap-1 text-xs text-n-500 cursor-pointer">
                            <input
                              type="radio"
                              name="primaryChannel"
                              checked={isPrimary}
                              onChange={() => setPrimaryChannelId(ch.id)}
                              className="w-3 h-3 accent-yellow-400"
                            />
                            Principal
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {err && <p className="text-sm text-s-red">{err}</p>}
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-800 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
                {saving ? '...' : 'Guardar'}
              </button>
              <button type="button" onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600"
                style={{ background: 'rgba(255,255,255,0.05)' }}>
                Cancelar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {modal === 'reset-pw' && selectedUser && (
        <Modal title={`Reset Contraseña: ${selectedUser.username}`} onClose={closeModal}>
          <form onSubmit={handleResetPw} className="space-y-4">
            <InputField label="Nueva contraseña" type="password" value={pwForm.newPassword}
              onChange={e => setPwForm(f => ({...f, newPassword: e.target.value}))} minLength={6} required />
            {err && <p className="text-sm text-s-red">{err}</p>}
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-800 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
                {saving ? '...' : 'Resetear'}
              </button>
              <button type="button" onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600"
                style={{ background: 'rgba(255,255,255,0.05)' }}>
                Cancelar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Import CSV Modal */}
      {modal === 'import' && (
        <Modal title="Importar usuarios (CSV)" onClose={closeModal}>
          <div className="space-y-3">
            <p className="text-xs text-n-500">
              Cabecera esperada: <code className="font-mono">username,email,role</code>. Campos extra son ignorados.
              Si no se incluye <code>password</code>, se genera una temporal.
            </p>
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleCsvFile(e.dataTransfer.files?.[0]); }}
              className="rounded-md p-4 text-center text-xs text-n-500 border-2 border-dashed border-n-200 cursor-pointer"
            >
              <input type="file" accept=".csv,text/csv" onChange={(e) => handleCsvFile(e.target.files?.[0])}
                className="block mx-auto text-xs" />
              <p className="mt-2">o pegá el contenido abajo</p>
            </div>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="username,email,role\njuan,juan@op.com,user\nmaria,maria@op.com,admin"
              rows={6}
              className="w-full text-xs font-mono p-2 rounded-sm border border-n-200"
            />
            {importResults && (
              <div className="text-xs">
                <p className="font-semibold text-n-700 mb-1">Resultado: {importResults.created}/{importResults.count}</p>
                <div className="max-h-40 overflow-y-auto">
                  {importResults.results.map((r, i) => (
                    <div key={i} className="flex justify-between gap-2 py-0.5">
                      <span className="text-n-600">{r.username || r.email}</span>
                      {r.ok ? (
                        <span className="text-s-green font-mono">{r.tempPassword || 'OK'}</span>
                      ) : (
                        <span className="text-s-red">{r.error}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {err && <p className="text-sm text-s-red">{err}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={handleImportSubmit} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
                {saving ? '...' : 'Importar'}
              </button>
              <button onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600"
                style={{ background: 'rgba(0,0,0,0.04)' }}>
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Invite Modal */}
      {modal === 'bulk-invite' && (
        <Modal title="Invitar en bulk" onClose={closeModal}>
          <div className="space-y-3">
            <p className="text-xs text-n-500">Pegá emails separados por coma, espacio o salto de línea.</p>
            <textarea
              value={bulkInviteEmails}
              onChange={(e) => setBulkInviteEmails(e.target.value)}
              placeholder="juan@op.com, maria@op.com\npedro@op.com"
              rows={6}
              className="w-full text-xs font-mono p-2 rounded-sm border border-n-200"
            />
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Rol</label>
              <select value={bulkInviteRole} onChange={(e) => setBulkInviteRole(e.target.value)}
                className="w-full px-3 py-2 rounded-md text-sm border border-n-200">
                <option value="user">Usuario</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {importResults && (
              <div className="text-xs">
                <p className="font-semibold text-n-700 mb-1">Resultado: {importResults.created}/{importResults.count}</p>
                <div className="max-h-40 overflow-y-auto">
                  {importResults.results.map((r, i) => (
                    <div key={i} className="flex justify-between gap-2 py-0.5">
                      <span className="text-n-600">{r.username || r.email}</span>
                      {r.ok ? <span className="text-s-green font-mono">{r.tempPassword}</span> : <span className="text-s-red">{r.error}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {err && <p className="text-sm text-s-red">{err}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={handleBulkInvite} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#34C759,#007AFF)' }}>
                {saving ? '...' : 'Invitar'}
              </button>
              <button onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600"
                style={{ background: 'rgba(0,0,0,0.04)' }}>
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Reset Password Modal */}
      {modal === 'bulk-reset' && (
        <Modal title={`Reset password (${selected.size})`} onClose={closeModal}>
          <div className="space-y-3">
            <p className="text-xs text-n-500">Esto generará una contraseña temporal para cada usuario seleccionado.</p>
            {bulkResetResults && (
              <div className="text-xs">
                <p className="font-semibold text-n-700 mb-1">Resultado: {bulkResetResults.results.filter(r => r.ok).length}/{bulkResetResults.count}</p>
                <div className="max-h-44 overflow-y-auto">
                  {bulkResetResults.results.map((r, i) => (
                    <div key={i} className="flex justify-between gap-2 py-0.5">
                      <span className="text-n-600">{r.username || r.id}</span>
                      {r.ok ? <span className="text-s-green font-mono">{r.tempPassword}</span> : <span className="text-s-red">{r.error}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {err && <p className="text-sm text-s-red">{err}</p>}
            <div className="flex gap-2 pt-1">
              {!bulkResetResults && (
                <button onClick={handleBulkReset} disabled={saving}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#FF9F0A,#FF9500)' }}>
                  {saving ? '...' : 'Confirmar reset'}
                </button>
              )}
              <button onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600"
                style={{ background: 'rgba(0,0,0,0.04)' }}>
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === 'delete' && selectedUser && (
        <Modal title="Confirmar Eliminación" onClose={closeModal}>
          <div className="flex items-start gap-3 p-4 rounded-xl mb-5"
            style={{ background: 'rgba(255,55,95,0.10)', border: '1px solid rgba(255,55,95,0.20)' }}>
            <AlertTriangle size={16} className="text-s-red mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-n-700">¿Eliminar al usuario <strong className="text-s-red">{selectedUser.username}</strong>?</p>
              <p className="text-xs text-n-500 mt-1">Esta acción no se puede deshacer.</p>
            </div>
          </div>
          {err && <p className="text-sm text-s-red mb-3">{err}</p>}
          <div className="flex gap-2">
            <button onClick={handleDelete} disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'rgba(255,107,107,0.8)' }}>
              {saving ? '...' : 'Eliminar'}
            </button>
            <button onClick={closeModal}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600"
              style={{ background: 'rgba(255,255,255,0.05)' }}>
              Cancelar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
